// Copyright 2021-2026 The Connect Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { createClient } from "@connectrpc/connect";
import { createChatView } from "./demo/chat-view.js";
import { requireElement } from "./demo/dom.js";
import { highlightCodeExamples } from "./demo/highlight.js";
import { createStreamsView } from "./demo/streams-view.js";
import { initTabs } from "./demo/tabs.js";
import { createDemoTransport } from "./demo/transport.js";
import type { StreamingTransportChoice } from "./demo/transport.js";
import {
  isWebTransportSupported,
  probeServerWebTransport,
} from "./demo/webtransport-support.js";
import { SwappableTransport } from "./demo/swappable-transport.js";
import { ElizaService } from "./gen/connectbidi/eliza/v1/eliza_pb.js";

function transportLabel(
  choice: StreamingTransportChoice,
  connectionPerStream: boolean,
): string {
  if (choice === "webtransport") {
    return "WebTransport";
  }
  return connectionPerStream
    ? "WebSocket (connection per RPC)"
    : "WebSocket (multiplexed)";
}

/** Wires up the live demo: transport/server controls, tabs, and RPC views. */
function main(): void {
  highlightCodeExamples();

  // Transport tabs on the code example sections (WebSocket is the default).
  initTabs([
    {
      buttonId: "code-tab-btn-ts-websocket",
      panelId: "code-panel-ts-websocket",
    },
    {
      buttonId: "code-tab-btn-ts-webtransport",
      panelId: "code-panel-ts-webtransport",
    },
  ]);
  initTabs([
    {
      buttonId: "code-tab-btn-go-websocket",
      panelId: "code-panel-go-websocket",
    },
    {
      buttonId: "code-tab-btn-go-webtransport",
      panelId: "code-panel-go-webtransport",
    },
  ]);

  const transportSelect = requireElement<HTMLSelectElement>(
    "#transport-select",
  );
  const unsupportedBadge = requireElement<HTMLElement>(
    "#transport-unsupported-badge",
  );

  // The demo always talks to its own origin; the server address is not
  // user-configurable.
  const serverUrl = window.location.origin;

  const webTransportOption = transportSelect.querySelector<HTMLOptionElement>(
    'option[value="webtransport"]',
  );
  const webTransportLabel = webTransportOption?.innerText ?? "WebTransport";
  const realitySection = requireElement<HTMLElement>("#webtransport-reality");

  function setWebTransportAvailable(available: boolean): void {
    if (webTransportOption !== null) {
      webTransportOption.disabled = !available;
      webTransportOption.innerText = available
        ? webTransportLabel
        : "WebTransport (unavailable here)";
    }
    unsupportedBadge.classList.toggle("hidden", available);
    // The "why is WebTransport not shown?" section (the badge's anchor
    // target) only appears where WebTransport is actually unavailable, e.g.
    // the Cloudflare Workers deployment.
    realitySection.classList.toggle("hidden", available);
    if (!available && transportSelect.value === "webtransport") {
      transportSelect.value = "websocket";
      applyTransportChange();
    }
  }

  /**
   * Whether WebTransport can possibly work with the current browser and
   * server URL, without asking the server. The constructor itself throws
   * synchronously on non-https URLs (e.g. `wrangler dev` on
   * http://localhost:8787), so this must be checked before ever building
   * a WebTransport-backed transport.
   */
  function webTransportPossible(): boolean {
    return isWebTransportSupported() && serverUrl.startsWith("https:");
  }

  /**
   * WebTransport needs support on both ends: the API in this browser, and
   * an HTTP/3 endpoint on the selected server (a Cloudflare Workers
   * deployment, for example, only offers WebSocket).
   */
  async function refreshWebTransportAvailability(): Promise<void> {
    if (!webTransportPossible()) {
      setWebTransportAvailable(false);
      return;
    }
    setWebTransportAvailable(await probeServerWebTransport(serverUrl));
  }

  function currentChoice(): StreamingTransportChoice {
    return transportSelect.value === "webtransport"
      ? "webtransport"
      : "websocket";
  }

  // The third dropdown option is still the WebSocket transport, just with
  // a dedicated connection per streaming RPC instead of multiplexing.
  function connectionPerStream(): boolean {
    return transportSelect.value === "websocket-per-rpc";
  }

  // Pick a safe initial choice before building any transport: the select
  // may default to WebTransport, and constructing an impossible transport
  // throws synchronously, which would take the whole demo down with it.
  // The badge and dropdown state are reconciled by the
  // refreshWebTransportAvailability() call further down, once the swap
  // machinery it pokes actually exists.
  if (!webTransportPossible()) {
    transportSelect.value = "websocket";
  }

  const initial = createDemoTransport(currentChoice(), serverUrl, {
    connectionPerStream: connectionPerStream(),
  });
  const swappable = new SwappableTransport(initial.transport);
  const client = createClient(ElizaService, swappable);

  const streamsView = createStreamsView(client);
  streamsView.setTransportDescription(initial.description);

  const chatView = createChatView(
    client,
    requireElement<HTMLElement>("#chat-messages"),
    requireElement<HTMLElement>("#chat-input-container"),
  );
  void chatView.start();

  initTabs([
    { buttonId: "tab-btn-streams", panelId: "view-streams" },
    { buttonId: "tab-btn-chat", panelId: "view-chat" },
  ]);

  function applyTransportChange(): void {
    const choice = currentChoice();
    try {
      const next = createDemoTransport(choice, serverUrl, {
        connectionPerStream: connectionPerStream(),
      });
      // Running lanes hold streams on the old transport; stop them rather
      // than leaving them running against a transport that is no longer
      // selected.
      streamsView.stopStreams();
      swappable.swap(next.transport);
      streamsView.setTransportDescription(next.description);
      chatView.notifyTransportChanged(
        transportLabel(choice, connectionPerStream()),
      );
    } catch (err) {
      console.error("failed to switch transport:", err);
    }
  }

  transportSelect.addEventListener("change", applyTransportChange);

  // The only request the page makes on load. It never invokes the deployed
  // Worker: /capabilities.json is served from static assets (it isn't in
  // wrangler's run_worker_first list). Everything else — the WebSocket
  // connection, the WebTransport session, and every RPC — is dialed lazily,
  // on the visitor's first interaction with the demo.
  void refreshWebTransportAvailability();

}

main();
